// AgentVerse/common/schemas/collections/messagingContacts.js
import { z } from "zod";

const MessagingChannelSchema = z.object({
    channelId: z.string().uuid().optional().describe("Unique ID for this channel entry, generated by system."),
    channelType: z.enum([
        "app_direct",
        "agent_internal",
        "whatsapp",
        "sms",
        "email"
    ]).describe("The type of communication channel."),
    subscriptionId: z.string().uuid().optional().nullable(),
    identifier: z.string().min(1).describe("The unique identifier for this channel (e.g., appUserId for app_direct, agentName for agent_internal, phone number for whatsapp/sms)."),
    isPreferred: z.boolean().default(false).describe("Indicates if this is the preferred channel for outgoing messages."),
    canReceive: z.boolean().default(true).describe("Can this channel receive incoming messages?"),
    canSend: z.boolean().default(true).describe("Can messages be sent out via this channel?"),
    lastUsedAt: z.coerce.date().optional().describe("Timestamp of when this channel was last used for communication."),
    metadata: z.record(z.any()).optional().describe("Additional channel-specific metadata."),
});

const MessagingContactSchema = z.object({
    _id: z.string().optional().describe("MongoDB ObjectId"),
    contactId: z.string() // Server will generate this, so it's not min(3) here for input
        .optional() // Make optional for creation forms
        .describe("Globally unique, human-readable or system-generated ID for this contact. Auto-generated."),
    displayName: z.string().min(1, "Display Name is required.").describe("How the contact appears in UIs."),
    contactType: z.enum([
        "internal_user",
        "external_contact",
        "agent"
    ]).describe("The type of contact."),
    appUserId: z.string().optional().nullable().describe("AppBuilder User _id if contactType is 'internal_user'."),
    agentName: z.string().optional().nullable().describe("Registered agent name if contactType is 'agent'."),
    channels: z.array(MessagingChannelSchema).optional().default([]) // Default channels handled by service
        .describe("List of communication channels for this contact. Default channel auto-added for internal_user/agent."),
    avatarUrl: z.preprocess(
        (val) => {
            // if we get exactly an empty string, treat it as null
            if ((typeof val === "undefined" || val === null) || (typeof val === "string" && val.trim() === "")) return null;
            return val;
        },
        // now run the “normal” schema
        z.string().nullable()
    ).describe("URL to the contact's avatar image. Null if no avatar is set."),
    projectId: z.string().optional().nullable().default(null).describe("Should always be null. Contacts are global."),
    tags: z.array(z.string()).optional().default([]).describe("Tags for grouping contacts."),
    notes: z.string().optional().nullable().describe("Administrative notes about this contact."),
    isArchived: z.boolean().default(false).describe("Whether this contact is archived and should be hidden from active lists."),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
});

export default MessagingContactSchema;
export { MessagingChannelSchema };